import { packages as packagesDe } from './i18n/messages/de/packages.js';
import { packages as packagesEn } from './i18n/messages/en/packages.js';

/**
 * I-1977: a package names what a reader sees — its item types, field templates, field labels and
 * options, states, shared views, pages, blocks, roles, its flows' names and descriptions, its field
 * templates' descriptions and its flow nodes' reporter labels — either as a plain string, read as English,
 * or as `{ "en": …, "de": … }`, the form its flow output labels and checklist steps already take.
 *
 * The installer stores every name as its English words, so every record, upgrade comparison and
 * package key reads exactly as before; the other languages' words are kept with the install as a
 * catalogue from the English words, and the reader's locale picks from it when the name is shown.
 * A key is never a name: a state or role key is not in the catalogue and is never translated.
 *
 * Nothing here names a use case (AGM-1): the words are lines of a package file.
 */

/** A name in a package file: English alone, or English with its German words. */
export interface IPackageText {
  en: string;
  de?: string;
}

export type PackageTextType = string | IPackageText;

/** The languages a package may give a name in besides English. */
export const PACKAGE_TEXT_LOCALES = ['de'] as const;
export type PackageTextLocaleType = (typeof PACKAGE_TEXT_LOCALES)[number];

/**
 * A package's names in each other language, from their English words: `{ de: { "New": "Neu" } }`.
 * Kept with the install (`mapping.labels`) and answered by `GET packages/labels`.
 */
export type PackageLabelsType = Partial<Record<PackageTextLocaleType, Record<string, string>>>;

/** A package file with every name as its English words, and the catalogue of its other words. */
export interface IPackageNames {
  value: unknown;
  labels: PackageLabelsType;
  problems: string[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const listOf = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

/** A name in the reader's language: the package's words for `locale`, else the English it is stored as. */
export function packageLabelIn(
  text: string,
  labels: PackageLabelsType | null | undefined,
  locale: string,
): string {
  const words = labels?.[locale as PackageTextLocaleType];
  return (words && Object.prototype.hasOwnProperty.call(words, text) ? words[text] : null) ?? text;
}

/**
 * I-2496 (AGM-77): the word each package installed in a work area gives a role it maps onto, by the
 * role's key (`work-area-contributor` → `{ en: "Producer", de: "Produzent" }`). The role keeps its key
 * and its stored name; a screen, the CLI and MCP name it by these words, found by the key, never by
 * the English name. A reader's word is `packageTextIn(words[key], locale)`.
 */
export type PackageRoleWordsType = Record<string, IPackageText>;

/** The catalogues of several installs as one; an earlier install's words give way to a later one's. */
export function mergePackageLabels(
  catalogues: ReadonlyArray<PackageLabelsType | null | undefined>,
): PackageLabelsType {
  const merged: PackageLabelsType = {};
  for (const catalogue of catalogues) {
    for (const locale of PACKAGE_TEXT_LOCALES) {
      const words = catalogue?.[locale];
      if (words) merged[locale] = { ...(merged[locale] ?? {}), ...words };
    }
  }
  return merged;
}

/** Only the entries of `labels` whose English words are in `shown`: what a reader may already read. */
export function packageLabelsFor(
  labels: PackageLabelsType,
  shown: ReadonlySet<string>,
): PackageLabelsType {
  const kept: PackageLabelsType = {};
  for (const locale of PACKAGE_TEXT_LOCALES) {
    const words = Object.entries(labels[locale] ?? {}).filter(([en]) => shown.has(en));
    if (words.length > 0) kept[locale] = Object.fromEntries(words);
  }
  return kept;
}

/**
 * Every place a package file names something a reader sees, as `[the object, its name property,
 * where it is]`. Identifiers are not here: a block's `replaces` and a page's `removesBlocks` find a
 * block by the English title an earlier version stored, so they stay plain strings.
 */
function namedPlaces(
  pkg: Record<string, unknown>,
): Array<[Record<string, unknown>, string, string]> {
  const places: Array<[Record<string, unknown>, string, string]> = [];
  const each = (section: string, property: string) =>
    listOf(pkg[section]).forEach((entry, index) => {
      if (isRecord(entry)) places.push([entry, property, `${section}[${index}].${property}`]);
    });
  each('itemTypes', 'name');
  each('statuses', 'name');
  each('savedViews', 'name');
  each('roles', 'name');
  each('fieldTemplates', 'name');
  // I-2147: what a flow is called where flows are listed and chosen, and what it is for.
  each('flows', 'name');
  each('flows', 'description');
  // F-0621: the package's own title, shown in the packages list, its dialogs and its checklist.
  if ('title' in pkg) places.push([pkg, 'title', 'title']);
  // I-2147: the package's own description, shown in the packages list.
  if ('description' in pkg) places.push([pkg, 'description', 'description']);
  listOf(pkg.fieldTemplates).forEach((template, t) => {
    if (!isRecord(template)) return;
    if ('description' in template) {
      places.push([template, 'description', `fieldTemplates[${t}].description`]);
    }
    listOf(template.fields).forEach((field, f) => {
      if (!isRecord(field)) return;
      const at = `fieldTemplates[${t}].fields[${f}]`;
      for (const property of ['label', 'help', 'group']) {
        if (property in field) places.push([field, property, `${at}.${property}`]);
      }
      listOf(field.options).forEach((option, o) => {
        if (isRecord(option)) places.push([option, 'label', `${at}.options[${o}].label`]);
      });
    });
  });
  // I-1977: what a reporter or guest reads where an item is (a flow node's `reporterLabel`).
  listOf(pkg.flows).forEach((flow, f) => {
    const nodes = isRecord(flow) && isRecord(flow.definition) ? flow.definition.nodes : undefined;
    listOf(nodes).forEach((node, n) => {
      // I-2147: a node's name is what Branches, Reposition and the Trail call the step.
      if (isRecord(node) && 'name' in node) {
        places.push([node, 'name', `flows[${f}].definition.nodes[${n}].name`]);
      }
      if (isRecord(node) && 'reporterLabel' in node) {
        places.push([node, 'reporterLabel', `flows[${f}].definition.nodes[${n}].reporterLabel`]);
      }
    });
  });
  each('pages', 'title');
  listOf(pkg.pages).forEach((page, p) => {
    if (!isRecord(page)) return;
    listOf(page.blocks).forEach((block, b) => {
      if (isRecord(block) && 'title' in block) {
        places.push([block, 'title', `pages[${p}].blocks[${b}].title`]);
      }
    });
  });
  return places;
}

/**
 * A package file with each name as its English words, and its other words as a catalogue. A name in
 * the `{ en, de }` form needs English words, and German words when it gives them; two names with the
 * same English words must give the same German, since the reader's name is found by its English.
 * Anything else about a name (a missing one, a number) is left for `configurationPackageProblems`.
 */
export function packageNamesOf(raw: unknown): IPackageNames {
  if (!isRecord(raw)) return { value: raw, labels: {}, problems: [] };
  const value = structuredClone(raw);
  const labels: PackageLabelsType = {};
  const problems: string[] = [];
  for (const [owner, property, where] of namedPlaces(value)) {
    const name = owner[property];
    if (!isRecord(name)) continue;
    const extra = Object.keys(name).filter(
      (key) => key !== 'en' && !(PACKAGE_TEXT_LOCALES as readonly string[]).includes(key),
    );
    if (typeof name.en !== 'string' || name.en.trim() === '') {
      problems.push(`${where} needs its English words in "en".`);
      continue;
    }
    if (extra.length > 0) {
      problems.push(
        `${where} gives words in ${extra.map((key) => `"${key}"`).join(', ')}; a name takes "en" and "de".`,
      );
      continue;
    }
    owner[property] = name.en;
    for (const locale of PACKAGE_TEXT_LOCALES) {
      if (!(locale in name)) continue;
      const words = name[locale];
      if (typeof words !== 'string' || words.trim() === '') {
        problems.push(`${where} has empty words in "${locale}"; leave "${locale}" out instead.`);
        continue;
      }
      const catalogue = (labels[locale] ??= {});
      const known = catalogue[name.en];
      if (known !== undefined && known !== words) {
        problems.push(
          `${where} reads "${name.en}" in English like another name, but "${words}" in "${locale}" where that one reads "${known}"; give both the same words.`,
        );
        continue;
      }
      catalogue[name.en] = words;
    }
  }
  return { value, labels, problems };
}

/**
 * F-0794 S9 (PIO-23): `packageNamesOf` the other way round — a package file whose names are English
 * words, with each name the catalogue gives other words for written as `{ en, de }` again, so a
 * package read back from a work area names everything as the file it was installed from did. A name
 * the catalogue has no words for stays a plain string. The file is not changed.
 */
export function packageWordsOf<T>(raw: T, labels: PackageLabelsType | null | undefined): T {
  if (!isRecord(raw)) return raw;
  const value = structuredClone(raw) as Record<string, unknown>;
  for (const [owner, property] of namedPlaces(value)) {
    const name = owner[property];
    if (typeof name !== 'string') continue;
    const words: Partial<Record<PackageTextLocaleType, string>> = {};
    for (const locale of PACKAGE_TEXT_LOCALES) {
      const catalogue = labels?.[locale];
      if (catalogue && Object.prototype.hasOwnProperty.call(catalogue, name)) {
        words[locale] = catalogue[name];
      }
    }
    if (Object.keys(words).length > 0) owner[property] = { en: name, ...words };
  }
  return value as T;
}

/**
 * A package file with every name as its English words, and its example content in English
 * (`packageSeedIn`, T-0879), for a reader that only needs those.
 */
export function withEnglishNames<T>(raw: T): T {
  const value = packageNamesOf(raw).value;
  if (isRecord(value) && value.seed !== undefined) value.seed = packageSeedIn(value.seed, 'en');
  return value as T;
}

/** The `{ en, de }` form of a text, as a name takes it: English words, and at most German beside. */
function isPackageText(value: unknown): value is IPackageText {
  return (
    isRecord(value) &&
    typeof value.en === 'string' &&
    Object.keys(value).every(
      (key) => key === 'en' || (PACKAGE_TEXT_LOCALES as readonly string[]).includes(key),
    )
  );
}

/** A text in one language: its words for `locale`, else its English; a plain string is English. */
export function packageTextIn(text: unknown, locale: string): unknown {
  if (!isPackageText(text)) return text;
  const words = text[locale as PackageTextLocaleType];
  return typeof words === 'string' && words.trim() !== '' ? words : text.en;
}

/**
 * The places of a package's example content (`seed`) that hold words a person reads: each item's
 * title and body and its text field values, and each decision's title, background, options,
 * recommendation and what waits on it.
 */
function seedTextPlaces(seed: Record<string, unknown>): Array<[Record<string, unknown>, string]> {
  const places: Array<[Record<string, unknown>, string]> = [];
  for (const item of listOf(seed.items)) {
    if (!isRecord(item)) continue;
    places.push([item, 'title'], [item, 'content']);
    if (isRecord(item.values)) {
      for (const field of Object.keys(item.values)) places.push([item.values, field]);
    }
  }
  for (const decision of listOf(seed.decisions)) {
    if (!isRecord(decision)) continue;
    for (const property of ['title', 'background', 'recommendation', 'blocksText']) {
      places.push([decision, property]);
    }
    for (const option of listOf(decision.options)) {
      if (isRecord(option)) places.push([option, 'label'], [option, 'consequence']);
    }
  }
  return places;
}

/**
 * T-0879: a package's example content in the installer's language. Unlike a name, example content is
 * written as records the work area owns and edits — an item's title and body, its field values, a
 * decision — so its words are picked once, when it is written, in the locale of the person who
 * installs it, falling back to English. Each text is a plain string (English) or `{ en, de }`.
 * The file is not changed.
 */
export function packageSeedIn<T>(seed: T, locale: string): T {
  if (!isRecord(seed)) return seed;
  const copy = structuredClone(seed) as Record<string, unknown>;
  for (const [owner, property] of seedTextPlaces(copy)) {
    if (property in owner) owner[property] = packageTextIn(owner[property], locale);
  }
  return copy as T;
}

/** Why a text of the example content is not a plain string or `{ en, de }`, one sentence each. */
export function packageSeedTextProblems(seed: unknown): string[] {
  if (!isRecord(seed)) return [];
  const problems: string[] = [];
  for (const [owner, property] of seedTextPlaces(seed)) {
    const text = owner[property];
    // Only a text in the `{ en, de }` form; a field value of another shape is the field's to judge.
    if (!isRecord(text) || !('en' in text)) continue;
    const named = typeof owner.key === 'string' ? `"${owner.key}"` : 'an entry';
    if (!isPackageText(text) || text.en.trim() === '') {
      problems.push(
        `The example content’s ${property} of ${named} gives its words as "en" and, besides, at most "de".`,
      );
    } else if ('de' in text && (typeof text.de !== 'string' || text.de.trim() === '')) {
      problems.push(
        `The example content’s ${property} of ${named} has empty words in "de"; leave "de" out instead.`,
      );
    }
  }
  return problems;
}

/**
 * I-1971: the line under each item a package's example content makes, in the language the content
 * is written in (T-0879), naming the package by its title in that language. Written once at install.
 */
export function packageExampleFooter(
  title: string,
  labels: PackageLabelsType | null | undefined,
  locale: string,
): string {
  const words = locale === 'de' ? packagesDe.exampleFooter : packagesEn.exampleFooter;
  // A function, so a title holding `$&` or `$'` is written as it is, not read as a pattern.
  const named = packageLabelIn(title, labels, locale);
  return words.replace('{title}', () => named);
}
